import React, { useCallback, useState } from 'react'
import { TokenList, Version } from '@uniswap/token-lists'
import { SlCheckbox, SlButton } from '@shoelace-style/shoelace/dist/react'
import styled from 'styled-components'
import { Link, Message, Text } from '@nominex/dex-uikit'
import { AutoColumn } from 'components/Layout'
import { ListLogo } from 'components/Logo'
import { useAppDispatch } from 'state'
import useFetchListCallback from 'hooks/useFetchListCallback'
import { enableList, removeList } from 'state/lists/actions'
import { useAllLists } from 'state/lists/hooks'
import { useTranslation } from 'contexts/Localization'

interface ImportProps {
  listURL: string
  list: TokenList
  onImport: () => void
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`

function listVersionLabel(version: Version): string {
  return `v${version.major}.${version.minor}.${version.patch}`
}

function ImportList({ listURL, list, onImport }: ImportProps) {
  const dispatch = useAppDispatch()

  const { t } = useTranslation()

  // user must accept
  const [confirmed, setConfirmed] = useState(false)

  const lists = useAllLists()
  const fetchList = useFetchListCallback()

  // monitor is list is loading
  const adding = Boolean(lists[listURL]?.loadingRequestId)
  const [addError, setAddError] = useState<string | null>(null)

  const handleAddList = useCallback(() => {
    if (adding) return
    setAddError(null)
    fetchList(listURL)
      .then(() => {
        dispatch(enableList(listURL))
        onImport()
      })
      .catch((error) => {
        setAddError(error.message)
        dispatch(removeList(listURL))
      })
  }, [adding, dispatch, fetchList, listURL, onImport])

  return (
    <Wrapper>
      <AutoColumn gap="md">
        <AutoColumn gap="md">
          <div className="bg-dark-gray-900 text-white rounded-12 px-12 py-16">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-8">
                <ListLogo size="40px" logoURI={list.logoURI} alt={`${list.name} list logo`} />
                <div className="flex flex-col justify-between">
                  <div className="flex items-center">
                    <span className="text-18 font-medium">{list.name}</span>
                  </div>
                  <div className="text-12 lowercase text-dark-gray-100 flex items-center gap-8">
                    <span>{listVersionLabel(list.version)}</span>
                    <span>
                      {list.tokens.length} {t('Tokens')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Link small external ellipsis maxWidth="90%" href={`https://tokenlists.org/token-list?url=${listURL}`}>
            {listURL}
          </Link>

          <Message variant="danger">
            <div className="text-14">
              <h5 className="text-red text-16 font-semibold mb-16">{t('Import at your own risk')}</h5>
              <p className="text-red mb-8">
                {t(
                  'By adding this list you are implicitly trusting that the data is correct. Anyone can create a list, including creating fake versions of existing lists and lists that claim to represent projects that do not have one.',
                )}
              </p>
              <p className="text-red">
                {t('If you purchase a token from this list, you may not be able to sell it back.')}
              </p>
            </div>
          </Message>

          <SlCheckbox checked={confirmed} onSlChange={() => setConfirmed(!confirmed)}>
            <span className="text-14 text-white">{t('I understand')}</span>
          </SlCheckbox>

          <SlButton variant="primary" disabled={!confirmed} onClick={handleAddList}>
            {t('Import list')}
          </SlButton>
          {addError ? (
            <Text color="failure" style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
              {addError}
            </Text>
          ) : null}
        </AutoColumn>
      </AutoColumn>
    </Wrapper>
  )
}

export default ImportList
